import { resolveDuration, TOAST_DEFAULTS, type ToastPosition } from "@lib/config/toastConfig";
import { useUISettings } from "@lib/stores/uiSettings";
import type { LucideIcon } from "lucide-react";
import { create } from "zustand";
import { immer } from "zustand/middleware/immer";

export type ToastType =
  | "success"
  | "error"
  | "warning"
  | "info"
  | "loading"
  | "undo"
  | "blocked"
  | "queued";

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface Toast {
  id: string;
  title: string;
  message?: string;
  type: ToastType;
  duration: number | "infinite";
  position: ToastPosition;
  exiting: boolean;
  morphing: boolean;
  startedAt: number;
  pausedAt: number | null;
  dedupeKey?: string;
  count: number;
  actions?: ToastAction[];
  /** Lucide icon override — falls back to the variant's default icon */
  icon?: LucideIcon;
}

// Timer registry — outside store state (not serializable)
const timers = new Map<string, ReturnType<typeof setTimeout>>();
function clearTimer(id: string) {
  const t = timers.get(id);
  if (t) {
    clearTimeout(t);
    timers.delete(id);
  }
}
function scheduleTimer(id: string, ms: number, onFire: (id: string) => void) {
  clearTimer(id);
  if (!Number.isFinite(ms)) return;
  timers.set(
    id,
    setTimeout(() => onFire(id), ms),
  );
}

export interface AddToastInput {
  title: string | ((count: number) => string);
  message?: string | ((count: number) => string);
  type: ToastType;
  duration?: number | "infinite";
  position?: ToastPosition;
  dedupeKey?: string;
  actions?: ToastAction[];
  /** Lucide icon override — falls back to the variant's default icon */
  icon?: LucideIcon;
}

interface ToastState {
  toasts: Toast[];
  addToast: (input: AddToastInput) => string;
  morphToast: (id: string, patch: Partial<Toast>, newDuration?: number) => void;
  removeToast: (id: string) => void;
  pauseToast: (id: string) => void;
  resumeToast: (id: string) => void;
  dismissAll: () => void;
}

export const useToastStore = create<ToastState>()(
  immer((set, get) => ({
    toasts: [],

    addToast: (input) => {
      // Dedupe — bump count + retitle + reschedule, don't stack
      if (input.dedupeKey) {
        const existing = get().toasts.find((t) => t.dedupeKey === input.dedupeKey && !t.exiting);
        if (existing) {
          get().morphToast(existing.id, {
            count: existing.count + 1,
            title:
              typeof input.title === "function" ? input.title(existing.count + 1) : input.title,
            message:
              typeof input.message === "function"
                ? input.message(existing.count + 1)
                : input.message,
          });
          return existing.id;
        }
      }

      const id = crypto.randomUUID();
      // User duration override comes from the canonical uiSettings store
      const { duration: userDuration } = useUISettings.getState().settings.notifications;
      const duration =
        input.type === "error"
          ? TOAST_DEFAULTS.errorDuration
          : (input.duration ?? resolveDuration(userDuration));
      const position = input.position ?? TOAST_DEFAULTS.position;

      set((state) => {
        state.toasts.push({
          id,
          type: input.type,
          title: typeof input.title === "function" ? input.title(1) : input.title,
          message: typeof input.message === "function" ? input.message(1) : input.message,
          duration,
          position,
          exiting: false,
          morphing: false,
          startedAt: Date.now(),
          pausedAt: null,
          dedupeKey: input.dedupeKey,
          count: 1,
          actions: input.actions,
          icon: input.icon,
        });
      });

      // maxCap — FIFO eviction, never evict paused (hovered) or exiting toast.
      // Outside the set() so removeToast (exit animation) can be reused —
      // the evicted toast slides out instead of vanishing abruptly.
      const positionToasts = get().toasts.filter((t) => t.position === position);
      if (positionToasts.length > TOAST_DEFAULTS.maxToasts) {
        const oldest = positionToasts.find((t) => t.pausedAt === null && !t.exiting);
        if (oldest) get().removeToast(oldest.id);
      }

      if (input.type !== "loading" && typeof duration === "number") {
        scheduleTimer(id, duration, (tid) => get().removeToast(tid));
      }
      return id;
    },

    morphToast: (id, patch, newDuration) => {
      set((state) => {
        const t = state.toasts.find((t) => t.id === id);
        if (!t) return;
        Object.assign(t, patch, { morphing: true, startedAt: Date.now(), pausedAt: null });
        if (newDuration !== undefined) t.duration = newDuration;
      });
      const t = get().toasts.find((t) => t.id === id);
      if (t && t.type !== "loading") {
        scheduleTimer(
          id,
          typeof t.duration === "number" ? t.duration : TOAST_DEFAULTS.duration,
          (tid) => get().removeToast(tid),
        );
      }
      setTimeout(
        () =>
          set((state) => {
            const t = state.toasts.find((t) => t.id === id);
            if (t) t.morphing = false;
          }),
        320,
      );
    },

    removeToast: (id) => {
      clearTimer(id);
      set((state) => {
        const t = state.toasts.find((t) => t.id === id);
        if (t) t.exiting = true;
      });
      setTimeout(
        () =>
          set((state) => {
            state.toasts = state.toasts.filter((t) => t.id !== id);
          }),
        200,
      );
    },

    pauseToast: (id) => {
      clearTimer(id);
      set((state) => {
        const t = state.toasts.find((t) => t.id === id);
        if (t) t.pausedAt = Date.now();
      });
    },

    resumeToast: (id) => {
      set((state) => {
        const t = state.toasts.find((t) => t.id === id);
        if (!t || t.pausedAt === null) return;
        const elapsed = t.pausedAt - t.startedAt;
        const remaining =
          typeof t.duration === "number" ? Math.max(t.duration - elapsed, 0) : Infinity;
        t.startedAt = Date.now();
        t.pausedAt = null;
        if (t.type !== "loading") scheduleTimer(id, remaining, (tid) => get().removeToast(tid));
      });
    },

    dismissAll: () => {
      get().toasts.forEach((t) => get().removeToast(t.id));
    },
  })),
);

export default useToastStore;
